/**
 * A lossless BibTeX reader and writer.
 *
 * The site's importer (`parseBibliographyImport`) turns a .bib file straight into
 * CSL-shaped entries, which is right for formatting and wrong for cleaning: a cleaner
 * has to give back the user's own file with only the requested changes, so it needs the
 * file as written — field order, `@string` macros, `#` concatenations, braced versus
 * quoted values, comments between entries. This module keeps all of that.
 *
 * Grammar, as BibTeX itself reads it:
 * - Anything outside an entry is a comment. `@comment{…}` is skipped brace-balanced.
 * - An entry is `@type{key, field = value, …}` or the same with parentheses.
 * - A value is one or more parts joined by `#`: `{braced {nested}}`, `"quoted"`, a bare
 *   number, or a macro name (from `@string`, or the month macros `jan`–`dec`).
 * - Types, field names and macro names are case-insensitive.
 *
 * Two leniencies, both in the direction of what the author meant: a backslash-escaped
 * brace or quote (`\{`, `\"`) does not count towards balancing, and a `%` line where a
 * field name is expected is skipped as a comment. A broken entry is reported with its
 * line number and kept verbatim, so cleaning never deletes text the parser could not
 * read.
 */

export interface BibValuePart {
  kind: "braced" | "quoted" | "number" | "macro";
  text: string;
}

export interface BibField {
  name: string;
  parts: BibValuePart[];
  line: number;
}

export interface BibEntry {
  kind: "entry";
  type: string;
  key: string;
  fields: BibField[];
  line: number;
}

export interface BibString {
  kind: "string";
  type: string;
  name: string;
  parts: BibValuePart[];
  line: number;
}

export interface BibPreamble {
  kind: "preamble";
  type: string;
  parts: BibValuePart[];
  line: number;
}

/** Text between entries (`at: false`) or an `@comment{…}` (`at: true`). */
export interface BibComment {
  kind: "comment";
  text: string;
  at: boolean;
  line: number;
}

/** An entry the reader could not finish, kept exactly as written. */
export interface BibBroken {
  kind: "broken";
  raw: string;
  line: number;
}

export type BibNode =
  | BibEntry
  | BibString
  | BibPreamble
  | BibComment
  | BibBroken;

export type BibErrorCode =
  | "missingDelimiter"
  | "missingKey"
  | "expectedFieldName"
  | "expectedEquals"
  | "expectedValue"
  | "expectedCommaOrClose"
  | "unbalancedBraces"
  | "unterminatedQuote"
  | "unterminatedEntry";

export interface BibParseError {
  code: BibErrorCode;
  line: number;
  /** The entry type and key, when known. */
  context?: string;
}

export interface BibDocument {
  nodes: BibNode[];
  errors: BibParseError[];
}

const RE_IDENTIFIER = /[^\s"#%'(),={}]+/y;
const RE_NUMBER = /\d+/y;
const RE_WHITESPACE = /\s/;
const RE_DIGIT = /\d/;
const RE_NEXT_ENTRY = /\n[ \t]*@/g;
const RE_ONLY_SPACE = /^\s*$/;

class ParseFailure extends Error {
  readonly code: BibErrorCode;
  readonly at: number;

  constructor(code: BibErrorCode, at: number) {
    super(code);
    this.code = code;
    this.at = at;
  }
}

class Reader {
  readonly text: string;
  pos = 0;
  private readonly lineStarts: number[] = [0];

  constructor(text: string) {
    this.text = text;
    for (let index = 0; index < text.length; index++) {
      if (text[index] === "\n") {
        this.lineStarts.push(index + 1);
      }
    }
  }

  /** 1-based line of a character offset. */
  lineAt(offset: number): number {
    let low = 0;
    let high = this.lineStarts.length - 1;
    while (low < high) {
      const middle = Math.ceil((low + high) / 2);
      if (this.lineStarts[middle] <= offset) {
        low = middle;
      } else {
        high = middle - 1;
      }
    }
    return low + 1;
  }

  get char(): string {
    return this.text[this.pos] ?? "";
  }

  get done(): boolean {
    return this.pos >= this.text.length;
  }

  skipSpace(): void {
    while (!this.done && RE_WHITESPACE.test(this.char)) {
      this.pos += 1;
    }
  }

  /** Whitespace and `%` line comments, where a field name is expected. */
  skipSpaceAndComments(): void {
    this.skipSpace();
    while (this.char === "%") {
      const end = this.text.indexOf("\n", this.pos);
      this.pos = end === -1 ? this.text.length : end + 1;
      this.skipSpace();
    }
  }

  readSticky(pattern: RegExp): string {
    pattern.lastIndex = this.pos;
    const match = pattern.exec(this.text);
    if (!match) {
      return "";
    }
    this.pos += match[0].length;
    return match[0];
  }

  /** Content of a `{…}` group starting at the cursor; leaves the cursor after it. */
  readBraced(): string {
    const open = this.pos;
    let depth = 0;
    while (!this.done) {
      const char = this.char;
      if (char === "\\") {
        this.pos += 2;
        continue;
      }
      if (char === "{") {
        depth += 1;
      } else if (char === "}") {
        depth -= 1;
        if (depth === 0) {
          this.pos += 1;
          return this.text.slice(open + 1, this.pos - 1);
        }
      }
      this.pos += 1;
    }
    throw new ParseFailure("unbalancedBraces", open);
  }

  /** Content of a `"…"` string; a quote inside braces does not end it. */
  readQuoted(): string {
    const open = this.pos;
    this.pos += 1;
    let depth = 0;
    while (!this.done) {
      const char = this.char;
      if (char === "\\") {
        this.pos += 2;
        continue;
      }
      if (char === "{") {
        depth += 1;
      } else if (char === "}") {
        depth -= 1;
      } else if (char === '"' && depth <= 0) {
        this.pos += 1;
        return this.text.slice(open + 1, this.pos - 1);
      }
      this.pos += 1;
    }
    throw new ParseFailure("unterminatedQuote", open);
  }

  readPart(): BibValuePart {
    const char = this.char;
    if (char === "{") {
      return { kind: "braced", text: this.readBraced() };
    }
    if (char === '"') {
      return { kind: "quoted", text: this.readQuoted() };
    }
    if (RE_DIGIT.test(char)) {
      return { kind: "number", text: this.readSticky(RE_NUMBER) };
    }
    const name = this.readSticky(RE_IDENTIFIER);
    if (name === "") {
      throw new ParseFailure("expectedValue", this.pos);
    }
    return { kind: "macro", text: name };
  }

  /** `part # part # …` */
  readValue(): BibValuePart[] {
    const parts: BibValuePart[] = [];
    for (;;) {
      this.skipSpace();
      parts.push(this.readPart());
      this.skipSpace();
      if (this.char !== "#") {
        return parts;
      }
      this.pos += 1;
    }
  }

  expect(char: string, code: BibErrorCode): void {
    if (this.char !== char) {
      throw new ParseFailure(code, this.pos);
    }
    this.pos += 1;
  }
}

function closerFor(open: string): string {
  return open === "(" ? ")" : "}";
}

/** The citation key: everything up to the first comma, or nothing if a field comes first. */
function readKey(reader: Reader, close: string): string | null {
  const start = reader.pos;
  while (!reader.done && reader.char !== "," && reader.char !== close) {
    if (reader.char === "=" || reader.char === "\n") {
      break;
    }
    reader.pos += 1;
  }
  if (reader.char === "=" || (reader.char === "\n" && reader.done)) {
    reader.pos = start;
    return null;
  }
  return reader.text.slice(start, reader.pos).trim();
}

function readFields(reader: Reader, close: string): BibField[] {
  const fields: BibField[] = [];
  for (;;) {
    reader.skipSpaceAndComments();
    if (reader.done) {
      throw new ParseFailure("unterminatedEntry", reader.pos);
    }
    if (reader.char === ",") {
      reader.pos += 1;
      continue;
    }
    if (reader.char === close) {
      reader.pos += 1;
      return fields;
    }
    const line = reader.lineAt(reader.pos);
    const name = reader.readSticky(RE_IDENTIFIER);
    if (name === "") {
      throw new ParseFailure("expectedFieldName", reader.pos);
    }
    reader.skipSpace();
    reader.expect("=", "expectedEquals");
    const parts = reader.readValue();
    fields.push({ name, parts, line });
    reader.skipSpaceAndComments();
    if (reader.char !== "," && reader.char !== close) {
      throw new ParseFailure(
        reader.done ? "unterminatedEntry" : "expectedCommaOrClose",
        reader.pos
      );
    }
  }
}

function readEntry(
  reader: Reader,
  type: string,
  close: string,
  line: number,
  errors: BibParseError[]
): BibEntry {
  reader.skipSpace();
  const key = readKey(reader, close);
  if (key === null || key === "") {
    errors.push({ code: "missingKey", line, context: `@${type}` });
  }
  if (reader.char === ",") {
    reader.pos += 1;
  }
  const fields = readFields(reader, close);
  return { kind: "entry", type, key: key ?? "", fields, line };
}

function readString(
  reader: Reader,
  type: string,
  close: string,
  line: number
): BibString {
  reader.skipSpace();
  const name = reader.readSticky(RE_IDENTIFIER);
  if (name === "") {
    throw new ParseFailure("expectedFieldName", reader.pos);
  }
  reader.skipSpace();
  reader.expect("=", "expectedEquals");
  const parts = reader.readValue();
  reader.skipSpace();
  if (reader.char === ",") {
    reader.pos += 1;
    reader.skipSpace();
  }
  reader.expect(close, "expectedCommaOrClose");
  return { kind: "string", type, name, parts, line };
}

function readPreamble(
  reader: Reader,
  type: string,
  close: string,
  line: number
): BibPreamble {
  const parts = reader.readValue();
  reader.skipSpace();
  reader.expect(close, "expectedCommaOrClose");
  return { kind: "preamble", type, parts, line };
}

/** `@comment` skips a balanced group, whichever delimiter opened it. */
function readCommentCommand(reader: Reader, line: number): BibComment {
  const open = reader.char;
  if (open === "{") {
    return { kind: "comment", text: reader.readBraced(), at: true, line };
  }
  const start = reader.pos + 1;
  const end = reader.text.indexOf(")", start);
  if (end === -1) {
    throw new ParseFailure("unterminatedEntry", reader.pos);
  }
  reader.pos = end + 1;
  return {
    kind: "comment",
    text: reader.text.slice(start, end),
    at: true,
    line,
  };
}

/** Parse the `@…` at the cursor. Returns null when the `@` is just text (an e-mail). */
function readCommand(reader: Reader, errors: BibParseError[]): BibNode | null {
  const at = reader.pos;
  const line = reader.lineAt(at);
  reader.pos += 1;
  reader.skipSpace();
  const type = reader.readSticky(RE_IDENTIFIER);
  reader.skipSpace();
  const open = reader.char;
  const lower = type.toLowerCase();
  if (type === "" || (open !== "{" && open !== "(")) {
    // A bare `@comment` comments out nothing in particular: BibTeX skips the word and
    // reads on, so it is text like any other — not an entry missing its brace.
    const lineStart = reader.text.lastIndexOf("\n", at - 1) + 1;
    const startsLine = RE_ONLY_SPACE.test(reader.text.slice(lineStart, at));
    if (startsLine && type !== "" && lower !== "comment") {
      throw new ParseFailure("missingDelimiter", at);
    }
    reader.pos = at + 1;
    return null;
  }
  if (lower === "comment") {
    return readCommentCommand(reader, line);
  }
  reader.pos += 1;
  const close = closerFor(open);
  if (lower === "string") {
    return readString(reader, type, close, line);
  }
  if (lower === "preamble") {
    return readPreamble(reader, type, close, line);
  }
  return readEntry(reader, type, close, line, errors);
}

/** Where to resume after a broken entry: the next `@` that starts a line. */
function recoveryPoint(text: string, from: number): number {
  RE_NEXT_ENTRY.lastIndex = from;
  const match = RE_NEXT_ENTRY.exec(text);
  return match ? match.index + 1 : text.length;
}

function pushText(nodes: BibNode[], text: string, line: number): void {
  if (RE_ONLY_SPACE.test(text)) {
    return;
  }
  const last = nodes.at(-1);
  if (last?.kind === "comment" && !last.at) {
    last.text += text;
    return;
  }
  nodes.push({ kind: "comment", text, at: false, line });
}

export function parseBibtex(text: string): BibDocument {
  const reader = new Reader(text);
  const nodes: BibNode[] = [];
  const errors: BibParseError[] = [];
  while (!reader.done) {
    const at = text.indexOf("@", reader.pos);
    if (at === -1) {
      pushText(nodes, text.slice(reader.pos), reader.lineAt(reader.pos));
      break;
    }
    pushText(nodes, text.slice(reader.pos, at), reader.lineAt(reader.pos));
    reader.pos = at;
    try {
      const node = readCommand(reader, errors);
      if (node) {
        nodes.push(node);
      } else {
        pushText(nodes, "@", reader.lineAt(at));
      }
    } catch (error) {
      if (!(error instanceof ParseFailure)) {
        throw error;
      }
      const resume = recoveryPoint(text, at + 1);
      const raw = text.slice(at, resume).trimEnd();
      errors.push({
        code: error.code,
        line: reader.lineAt(error.at),
        context: raw.split("\n")[0].trim().slice(0, 60),
      });
      nodes.push({ kind: "broken", raw, line: reader.lineAt(at) });
      reader.pos = resume;
    }
  }
  return { nodes, errors };
}

/* ------------------------------------------------------------------- writing */

export function serializePart(part: BibValuePart, forceBraces = false): string {
  if (part.kind === "braced" || (forceBraces && part.kind === "quoted")) {
    return `{${part.text}}`;
  }
  if (part.kind === "quoted") {
    return `"${part.text}"`;
  }
  return part.text;
}

export function serializeValue(
  parts: readonly BibValuePart[],
  forceBraces = false
): string {
  return parts.map((part) => serializePart(part, forceBraces)).join(" # ");
}

export interface WriteOptions {
  /** Write quoted values in braces. */
  forceBraces?: boolean;
  indent?: string;
}

export function serializeEntry(
  entry: BibEntry,
  options: WriteOptions = {}
): string {
  const indent = options.indent ?? "  ";
  const width = Math.max(0, ...entry.fields.map((field) => field.name.length));
  const lines = entry.fields.map(
    (field) =>
      `${indent}${field.name.padEnd(width)} = ${serializeValue(field.parts, options.forceBraces)}`
  );
  const head = `@${entry.type}{${entry.key}`;
  return lines.length === 0 ? `${head}}` : `${head},\n${lines.join(",\n")}\n}`;
}

export function serializeNode(
  node: BibNode,
  options: WriteOptions = {}
): string {
  switch (node.kind) {
    case "entry":
      return serializeEntry(node, options);
    case "string":
      return `@${node.type}{${node.name} = ${serializeValue(node.parts, options.forceBraces)}}`;
    case "preamble":
      return `@${node.type}{${serializeValue(node.parts)}}`;
    case "comment":
      return node.at ? `@comment{${node.text}}` : node.text.trim();
    default:
      return node.raw;
  }
}

/* -------------------------------------------------------------- reading values */

/** The literal text of a value, with macros expanded where a definition is known. */
export function valueText(
  parts: readonly BibValuePart[],
  macros: ReadonlyMap<string, string> = MONTH_MACROS
): string {
  return parts
    .map((part) =>
      part.kind === "macro"
        ? (macros.get(part.text.toLowerCase()) ?? part.text)
        : part.text
    )
    .join("");
}

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const MONTH_MACRO_NAMES = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

/** BibTeX's standard styles define `jan`…`dec` as the full English month names. */
export const MONTH_MACROS: ReadonlyMap<string, string> = new Map(
  MONTH_MACRO_NAMES.map((name, index) => [name, MONTH_NAMES[index]])
);

/** Expand `@string` definitions in document order (a macro may use an earlier one). */
export function collectMacros(nodes: readonly BibNode[]): Map<string, string> {
  const macros = new Map<string, string>(MONTH_MACROS);
  for (const node of nodes) {
    if (node.kind === "string") {
      macros.set(node.name.toLowerCase(), valueText(node.parts, macros));
    }
  }
  return macros;
}

export function fieldOf(entry: BibEntry, name: string): BibField | undefined {
  const lower = name.toLowerCase();
  return entry.fields.find((field) => field.name.toLowerCase() === lower);
}

/** A field's text with macros expanded, or "" when the entry does not have it. */
export function fieldText(
  entry: BibEntry,
  name: string,
  macros?: ReadonlyMap<string, string>
): string {
  const field = fieldOf(entry, name);
  return field ? valueText(field.parts, macros).trim() : "";
}

/* ------------------------------------------------------------- LaTeX → Unicode */

/** Combining marks for TeX's accent commands. */
const ACCENTS: Record<string, string> = {
  "`": "̀",
  "'": "́",
  "^": "̂",
  "~": "̃",
  "=": "̄",
  u: "̆",
  ".": "̇",
  '"': "̈",
  r: "̊",
  H: "̋",
  v: "̌",
  d: "̣",
  c: "̧",
  k: "̨",
  b: "̱",
};

const SYMBOLS: Record<string, string> = {
  ss: "ß",
  ae: "æ",
  AE: "Æ",
  oe: "œ",
  OE: "Œ",
  aa: "å",
  AA: "Å",
  o: "ø",
  O: "Ø",
  l: "ł",
  L: "Ł",
  i: "ı",
  j: "ȷ",
  dh: "ð",
  DH: "Ð",
  th: "þ",
  TH: "Þ",
  ng: "ŋ",
  NG: "Ŋ",
  textendash: "–",
  textemdash: "—",
  textquoteleft: "‘",
  textquoteright: "’",
  textquotedblleft: "“",
  textquotedblright: "”",
  ldots: "…",
  dots: "…",
  textellipsis: "…",
  textregistered: "®",
  texttrademark: "™",
  textcopyright: "©",
  textdegree: "°",
  S: "§",
  P: "¶",
  LaTeX: "LaTeX",
  TeX: "TeX",
  BibTeX: "BibTeX",
  alpha: "α",
  beta: "β",
  gamma: "γ",
  delta: "δ",
  epsilon: "ε",
  varepsilon: "ε",
  zeta: "ζ",
  eta: "η",
  theta: "θ",
  kappa: "κ",
  lambda: "λ",
  mu: "μ",
  nu: "ν",
  xi: "ξ",
  pi: "π",
  rho: "ρ",
  sigma: "σ",
  tau: "τ",
  phi: "φ",
  varphi: "φ",
  chi: "χ",
  psi: "ψ",
  omega: "ω",
  Gamma: "Γ",
  Delta: "Δ",
  Theta: "Θ",
  Lambda: "Λ",
  Pi: "Π",
  Sigma: "Σ",
  Phi: "Φ",
  Psi: "Ψ",
  Omega: "Ω",
  pm: "±",
  times: "×",
  leq: "≤",
  geq: "≥",
  neq: "≠",
  approx: "≈",
  infty: "∞",
};

const RE_SYMBOL_ACCENT =
  /\\([`'^~=."])\s*(?:\{\s*(\\[ij]|[A-Za-z])\s*\}|(\\[ij]|[A-Za-z]))/g;
const RE_LETTER_ACCENT =
  /\\([uvHckrdb])(?:\s*\{\s*(\\[ij]|[A-Za-z])\s*\}|\s+([A-Za-z]))/g;
const RE_ESCAPED_SPECIAL = /\\([&%$#_{}])/g;
const RE_FORMAT_COMMAND =
  /\\(?:emph|textit|textbf|textsc|textrm|textsf|texttt|textup|textmd|textnormal|mathrm|mathit|mathbf|mbox|hbox|url|href\{[^}]*\}|enquote|NoCaseChange|MakeUppercase|uppercase|textsuperscript|textsubscript)\s*(?=\{)/g;
const RE_NAMED_COMMAND = /\\([A-Za-z]+)(?:\{\})?\s?/g;
const RE_EM_DASH = /---/g;
const RE_EN_DASH = /--/g;
const RE_OPEN_DOUBLE = /``/g;
const RE_CLOSE_DOUBLE = /''/g;
const RE_OPEN_SINGLE = /`/g;
const RE_TIE = /~/g;
const RE_MATH_SHIFT = /\$/g;
const RE_SCRIPT = /[\^_](?=\{)/g;
const RE_BRACES = /[{}]/g;
const RE_SPACE_RUN = /\s+/g;
const ESCAPED_OPEN = "\uE000";
const ESCAPED_CLOSE = "\uE001";
const ESCAPED_DOLLAR = "\uE002";
const RE_ESCAPED_OPEN = /\uE000/g;
const RE_ESCAPED_CLOSE = /\uE001/g;
const RE_ESCAPED_DOLLAR = /\uE002/g;

/**
 * `\{`, `\}` and `\$` are literal characters, so they are parked as placeholders while
 * the grouping braces and math shifts around them are stripped.
 */
function escapedSpecial(char: string): string {
  if (char === "{") {
    return ESCAPED_OPEN;
  }
  if (char === "}") {
    return ESCAPED_CLOSE;
  }
  return char === "$" ? ESCAPED_DOLLAR : char;
}

function dotless(base: string): string {
  if (base === "\\i") {
    return "i";
  }
  return base === "\\j" ? "j" : base;
}

/**
 * Decode the LaTeX a .bib file carries into the Unicode Word and every other program
 * expects: `M{\"u}ller` → Müller, `\'{E}mile` → Émile, `--` → –, `{DNA}` → DNA.
 */
export function latexToUnicode(input: string): string {
  return input
    .replace(
      RE_SYMBOL_ACCENT,
      (_m, accent: string, braced?: string, bare?: string) =>
        `${dotless(braced ?? bare ?? "")}${ACCENTS[accent] ?? ""}`.normalize(
          "NFC"
        )
    )
    .replace(
      RE_LETTER_ACCENT,
      (_m, accent: string, braced?: string, bare?: string) =>
        `${dotless(braced ?? bare ?? "")}${ACCENTS[accent] ?? ""}`.normalize(
          "NFC"
        )
    )
    .replace(RE_ESCAPED_SPECIAL, (_m, char: string) => escapedSpecial(char))
    .replace(RE_FORMAT_COMMAND, "")
    .replace(RE_NAMED_COMMAND, (_m, name: string) => SYMBOLS[name] ?? "")
    .replace(RE_EM_DASH, "—")
    .replace(RE_EN_DASH, "–")
    .replace(RE_OPEN_DOUBLE, "“")
    .replace(RE_CLOSE_DOUBLE, "”")
    .replace(RE_OPEN_SINGLE, "‘")
    .replace(RE_TIE, " ")
    .replace(RE_MATH_SHIFT, "")
    .replace(RE_SCRIPT, "")
    .replace(RE_BRACES, "")
    .replace(RE_ESCAPED_OPEN, "{")
    .replace(RE_ESCAPED_CLOSE, "}")
    .replace(RE_ESCAPED_DOLLAR, "$")
    .replace(RE_SPACE_RUN, " ")
    .normalize("NFC")
    .trim();
}

/* ------------------------------------------------------------------- names */

export interface BibName {
  first: string;
  von: string;
  last: string;
  jr: string;
  /** A brace-protected group name — `{World Health Organization}`. */
  corporate: boolean;
}

const RE_AND_SEPARATOR = /\s+and\s+/iy;

/** Split at top-level `and`s; `{Barnes and Noble}` stays one name. */
export function splitBibtexNames(value: string): string[] {
  const names: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < value.length; index++) {
    const char = value[index];
    if (char === "{") {
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
    } else if (depth === 0 && RE_WHITESPACE.test(char)) {
      RE_AND_SEPARATOR.lastIndex = index;
      const match = RE_AND_SEPARATOR.exec(value);
      if (match) {
        names.push(value.slice(start, index));
        start = index + match[0].length;
        index = start - 1;
      }
    }
  }
  names.push(value.slice(start));
  return names.map((name) => name.trim()).filter(Boolean);
}

/** Top-level tokens and commas of one name. */
function nameParts(name: string): string[][] {
  const parts: string[][] = [[]];
  let token = "";
  let depth = 0;
  const flush = () => {
    if (token !== "") {
      parts.at(-1)?.push(token);
      token = "";
    }
  };
  for (const char of name) {
    if (char === "{") {
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
    }
    if (depth === 0 && (RE_WHITESPACE.test(char) || char === "~")) {
      flush();
    } else if (depth === 0 && char === ",") {
      flush();
      parts.push([]);
    } else {
      token += char;
    }
  }
  flush();
  return parts;
}

const RE_FIRST_LETTER = /\p{L}/u;

/** BibTeX's test: a token is "von" when its first letter is lower-case. */
function isLowerToken(token: string): boolean {
  if (token.startsWith("{") && !token.startsWith("{\\")) {
    return false;
  }
  const letter = RE_FIRST_LETTER.exec(latexToUnicode(token))?.[0];
  return (
    letter !== undefined &&
    letter === letter.toLowerCase() &&
    letter !== letter.toUpperCase()
  );
}

function join(tokens: readonly string[]): string {
  return latexToUnicode(tokens.join(" "));
}

/** "First von Last" with no comma. */
function parseNoComma(tokens: string[]): Omit<BibName, "corporate" | "jr"> {
  if (tokens.length === 1) {
    return { first: "", von: "", last: join(tokens) };
  }
  const head = tokens.slice(0, -1);
  const firstLower = head.findIndex(isLowerToken);
  if (firstLower === -1) {
    return { first: join(head), von: "", last: join(tokens.slice(-1)) };
  }
  let lastLower = firstLower;
  for (let index = firstLower; index < head.length; index++) {
    if (isLowerToken(head[index])) {
      lastLower = index;
    }
  }
  return {
    first: join(tokens.slice(0, firstLower)),
    von: join(tokens.slice(firstLower, lastLower + 1)),
    last: join(tokens.slice(lastLower + 1)),
  };
}

/** "von Last" before the first comma. */
function parseVonLast(tokens: string[]): { von: string; last: string } {
  let split = 0;
  for (let index = 0; index < tokens.length - 1; index++) {
    if (isLowerToken(tokens[index])) {
      split = index + 1;
    }
  }
  return { von: join(tokens.slice(0, split)), last: join(tokens.slice(split)) };
}

/** Read one BibTeX name: "First von Last", "von Last, First" or "von Last, Jr, First". */
export function parseBibtexName(raw: string): BibName {
  const name = raw.trim();
  const parts = nameParts(name).filter((part) => part.length > 0);
  const onlyToken =
    parts.length === 1 && parts[0].length === 1 ? parts[0][0] : "";
  if (onlyToken.startsWith("{") && onlyToken.endsWith("}")) {
    return {
      first: "",
      von: "",
      last: latexToUnicode(onlyToken),
      jr: "",
      corporate: true,
    };
  }
  if (parts.length === 0) {
    return { first: "", von: "", last: "", jr: "", corporate: false };
  }
  if (parts.length === 1) {
    return { ...parseNoComma(parts[0]), jr: "", corporate: false };
  }
  const { von, last } = parseVonLast(parts[0]);
  if (parts.length === 2) {
    return { first: join(parts[1]), von, last, jr: "", corporate: false };
  }
  return {
    first: join(parts.slice(2).flat()),
    von,
    last,
    jr: join(parts[1]),
    corporate: false,
  };
}

/** "others" closes a BibTeX name list that was cut short ("and others"). */
export function isOthers(name: string): boolean {
  return name.trim().toLowerCase() === "others";
}
